import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, Camera, ShieldCheck } from "lucide-react";

const DEMO_WORKER = "marcus-reed";

export default function ScanDemo() {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);

  const startScan = () => {
    if (scanning) return;
    setScanning(true);
    setTimeout(() => {
      navigate(`/verify/${DEMO_WORKER}`);
    }, 1000);
  };

  return (
    <div data-testid="scan-page" className="min-h-[calc(100vh-65px)] bg-[#F8FAFC]">
      <div className="mx-auto max-w-2xl px-6 py-10 md:py-14">
        <div className="mb-6 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600">
            <ScanLine className="h-3.5 w-3.5 text-[#FF5A1F]" strokeWidth={2.5} />
            ProCred / Foreman Scanner
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Gate-side · MVP
          </div>
        </div>

        <div className="border border-slate-200 bg-white p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Step 1
          </div>
          <h1 className="font-display mt-1 text-2xl font-bold tracking-tight text-[#0B1221] sm:text-3xl">
            Scan the worker&apos;s QR code
          </h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Point the camera at the worker&apos;s ProCred wallet. One scan returns a
            Ready for Site, Action Required, or Not Eligible answer.
          </p>

          {/* Camera-style viewport */}
          <div
            data-testid="scanner-viewport"
            className="relative mt-5 flex aspect-square w-full items-center justify-center overflow-hidden border border-slate-200 bg-slate-900"
          >
            {/* Corner brackets */}
            {[
              "top-4 left-4 border-l-2 border-t-2",
              "top-4 right-4 border-r-2 border-t-2",
              "bottom-4 left-4 border-l-2 border-b-2",
              "bottom-4 right-4 border-r-2 border-b-2",
            ].map((cls, i) => (
              <span key={i} className={`absolute h-10 w-10 border-[#FF5A1F] ${cls}`} />
            ))}

            {/* Crosshair */}
            <Camera className="h-10 w-10 text-white/60" strokeWidth={1.5} />

            {/* Scanline */}
            {scanning && (
              <div
                data-testid="scanner-line"
                className="pointer-events-none absolute left-0 right-0 top-0 h-[3px] bg-[#FF5A1F] shadow-[0_0_18px_4px_rgba(255,90,31,0.6)]"
                style={{
                  animation: "procred-scan 1s ease-in-out forwards",
                }}
              />
            )}

            {/* Status pill */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 border border-white/20 bg-black/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white backdrop-blur">
              <span className={`h-1.5 w-1.5 ${scanning ? "animate-pulse bg-[#FF5A1F]" : "bg-emerald-400"}`} />
              {scanning ? "Reading credentials…" : "Camera ready"}
            </div>
          </div>

          <style>{`
            @keyframes procred-scan {
              0%   { top: 0%; }
              100% { top: 100%; }
            }
          `}</style>

          <button
            type="button"
            data-testid="scan-demo-btn"
            onClick={startScan}
            disabled={scanning}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-[#FF5A1F] px-5 py-3.5 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#e94d14] disabled:opacity-70"
          >
            <ScanLine className="h-4 w-4" strokeWidth={2.5} />
            {scanning ? "Scanning…" : "Scan QR Code"}
          </button>

          <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-[#FF5A1F]" strokeWidth={2.25} />
            MVP build · simulated scan against Marcus Reed&apos;s sample wallet.
          </div>
        </div>
      </div>
    </div>
  );
}
